/**
 * OverageCrossoverNudge (Story 39.5)
 *
 * A savings-framed upgrade nudge shown when a flagged paid tenant is paying more
 * in per-client overage than the next tier would cost. The savings figure is
 * ADVISORY (computed from the rate table via computeCrossover) — it must NEVER
 * be co-located with a Stripe-derived charge (the add-client preview owns that,
 * F3/39-4). Never renders for free/Solo (computeCrossover returns no nudge) or
 * un-flagged tenants (the caller passes overageEnabled=false). Frequency:
 * on-becoming-relevant (render only when shouldNudge) — NOT a daily pester.
 */
import {
  computeCrossover,
  PLAN_DISPLAY_NAMES,
  type Plan,
} from "@provenleap/types";
import { Button } from "@provenleap/ui";

interface Props {
  plan: Plan;
  clientCount: number;
  interval: "monthly" | "annual";
  /** Per-tenant overage rollout flag — un-flagged tenants never see overage UI. */
  overageEnabled: boolean;
  onUpgrade: (nextTier: Exclude<Plan, "free">) => void;
}

export function OverageCrossoverNudge({
  plan,
  clientCount,
  interval,
  overageEnabled,
  onUpgrade,
}: Props) {
  if (!overageEnabled) return null;
  const advice = computeCrossover(plan, clientCount);
  if (!advice.shouldNudge || !advice.nextTier) return null;

  const next = advice.nextTier;
  const monthly = advice.monthlySavings;
  const savingsText =
    interval === "annual"
      ? `about $${Math.round(monthly * 10)}/yr`
      : `$${monthly.toFixed(2)}/mo`;

  return (
    <div
      className="!rounded-[22px] bg-muted/50 p-5 shadow-xs"
      style={{ border: "0.5px solid hsl(214 25% 70%)" }}
      data-testid="overage-crossover-nudge"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-1">
          <p className="text-[13px] font-semibold text-foreground">
            {monthly > 0
              ? `Upgrade to ${PLAN_DISPLAY_NAMES[next]} and save ${savingsText}`
              : `${PLAN_DISPLAY_NAMES[next]} now costs the same as your overage`}
          </p>
          <p className="text-[12px] text-muted-foreground">
            You have {clientCount} clients. At this size, {PLAN_DISPLAY_NAMES[next]}{" "}
            is {monthly > 0 ? "cheaper than" : "the same as"} your current plan plus
            per-client overage — and all your data carries over instantly.
          </p>
        </div>
        <Button
          variant="brand"
          className="shrink-0 rounded-2xl text-[13px] cursor-pointer"
          onClick={() => onUpgrade(next)}
        >
          Upgrade to {PLAN_DISPLAY_NAMES[next]}
        </Button>
      </div>
    </div>
  );
}
